<template>
  <div>
    <template v-if="viaje?.id">
      <!-- CARD PERSONA -->
      <div class="card-persona mb-10">
        <div
          class="persona-header d-flex align-items-center justify-content-between py-3 bg-primary-light"
          :class="[{ collapsed: isCollapsed }, 'px-20']"
          @click="toggleCollapse()">
          <div class="d-flex align-items-center flex-grow-1">
            <div class="icono-persona me-2">
              <IconOccupied
                :user="grupoCerrado.viaje.invitacionGrupo.usuarioDecisor"
                :all-colors="true"
                :thin-border="true" />
            </div>
            <div class="d-flex align-items-center flex-wrap">
              <span class="p medium" :class="'text-primary'">
                {{ grupoCerrado.viaje.invitacionGrupo.usuarioDecisor.nombre }}
                {{ grupoCerrado.viaje.invitacionGrupo.usuarioDecisor.apellidos }}
              </span>
            </div>
          </div>
          <CustomIcon :icon="isCollapsed ? arrowDownIcon : arrowUpIcon" size="14px" class="pointer text-primary" />
        </div>
        <transition name="collapse">
          <div v-show="!isCollapsed" class="collapse-content">
            <!-- SERVICIOS INCLUIDOS -->
            <div class="text-primary px-20 py-15">
              <div class="d-flex justify-content-between align-items-center mb-10">
                <span class="bold small-m p">
                  {{ grupoCerrado.viaje.invitacionGrupo.plazas }}x {{ $t('input.field.incluidPaqueteBasico') }}
                </span>
                <span class="bold text-primary small-m p">
                  {{ formatPrice(currency(grupoCerrado.viaje.invitacionGrupo.importeTotalEstimado)) }}
                  {{ agenciaStore.getCurrency }}
                </span>
              </div>
              <!-- ALOJAMIENTO -->
              <template v-if="viaje.hasAlojamiento">
                <div class="servicio-item mb-10 text-primary">
                  <div class="d-flex align-items-start">
                    <img
                      :src="viaje.cabeceraAlojamientos?.icono ?? accommodationIcon"
                      class="servicio-icon text-primary"
                      :style="{ width: '16px', height: '16px' }"
                      :alt="nombreAlojamiento" />
                    <div class="flex-grow-1">
                      <div class="d-flex align-items-center">
                        <div class="me-2 small-m alojamiento-nombre p">
                          {{ nombreAlojamiento }}
                        </div>
                      </div>
                      <div class="descripcion-servicio text-primary-light sub-m small">
                        {{ fechasAlojamiento }}
                      </div>
                    </div>
                  </div>
                </div>
              </template>
              <!-- SERVICIOS PAQUETE BASICO -->
              <template v-for="s in getServiciosPaqueteBasico()" :key="s.id">
                <div class="servicio-item mb-10 text-primary">
                  <div class="d-flex align-items-start">
                    <template v-if="getServiceIcon(s).type === TipoIconoServicio.IMAGE">
                      <img
                        :src="getServiceIcon(s).value"
                        class="servicio-icon text-primary"
                        :style="{ width: '16px', height: '16px' }"
                        :alt="s.nombre" />
                    </template>
                    <template v-if="getServiceIcon(s).type === TipoIconoServicio.ICON">
                      <CustomIcon :icon="getServiceIcon(s).value" class="servicio-icon text-primary" size="16px" />
                    </template>
                    <div class="flex-grow-1">
                      <div class="d-flex align-items-center">
                        <div class="translate mb-1 me-2 small-m p">
                          {{ s.nombre }}
                        </div>
                        <a
                          v-if="s.infoExtra || s.adjuntosPreventa.length > 0"
                          href="#"
                          class="text-primary sub text-decoration-underline pointer"
                          data-bs-toggle="modal"
                          :data-bs-target="`#infoExtraModalPB${s.id}`"
                          @click.prevent.stop>
                          + {{ $t('general.info') }}
                        </a>
                      </div>
                      <div v-if="s.descripcion" class="descripcion-servicio text-primary-light translate sub-m small">
                        {{ s.descripcion }}
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </transition>
      </div>

      <!-- IMPORTE TOTAL -->
      <div class="card-total-final px-20 py-15">
        <div class="d-flex justify-content-between align-items-center">
          <span class="p bold text-primary">{{ $t('subtitulo.importeTotal').toUpperCase() }}</span>
          <span class="h5 bold text-primary p-m semibold-m">
            {{ formatPrice(currency(grupoCerrado.viaje.invitacionGrupo.importeTotalEstimado)) }}
            {{ agenciaStore.getCurrency }}
          </span>
        </div>
      </div>
    </template>

    <!-- MODALES -->
    <Teleport to="body">
      <template
        v-for="s in getServiciosPaqueteBasico().filter(s => s.infoExtra || s.adjuntosPreventa.length > 0)"
        :key="`modal-incluido-${s.id}`">
        <InfoExtraModal
          :modal-id="`infoExtraModalPB${s.id}`"
          :title="s.nombre"
          :content="s.infoExtra"
          :adjuntos="s.adjuntosPreventa" />
      </template>
    </Teleport>
  </div>
</template>

<script setup>
  import { ref, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { formatPrice, numberOfDays } from '@/helpers/numberUtils';
  import { getCategoriasServiciosMetaData, TipoIconoServicio } from '@/helpers/serviciosUtils';
  import accommodationIcon from '@/assets/icons/alojamiento.svg';
  import CustomIcon from '@/components/CustomIcon.vue';
  import { informacionHabitacion } from '@/store_pinia/informacionHabitacion';
  import { agenciaStoreModule } from '@/store_pinia/agencia';
  import activityIcon from '@/assets/icons/servicios/actividad.svg';
  import currency from 'currency.js';
  import arrowDownIcon from '@/assets/icons/arrow_down.svg';
  import arrowUpIcon from '@/assets/icons/arrow_up.svg';
  import IconOccupied from '@/components/Icons/IconOccupied.vue';
  import { formatDate } from '@/helpers/dateUtiles';
  import InfoExtraModal from '@/components/modales/InfoExtraModal.vue';

  const { t } = useI18n();
  const storeHabitacion = informacionHabitacion();
  const agenciaStore = agenciaStoreModule();
  const isCollapsed = ref(false);

  defineProps({
    grupoCerrado: {
      type: Object,
      required: true,
    },
  });

  const viaje = computed(() => storeHabitacion.getViaje);
  const agrupacionesServicios = computed(() => storeHabitacion.getAgrupacionesServicios);
  const nombreAlojamiento = computed(() => {
    const numHoteles = viaje.value?.alojamientos.length ?? 0;
    if (numHoteles === 0) {
      return '';
    } else if (numHoteles === 1) {
      return viaje.value?.alojamientos[0].nombre;
    } else {
      return t('alojamiento.alojamientos');
    }
  });
  const fechasAlojamiento = computed(() => {
    const hotel = viaje.value?.alojamientos.find(h => h.usedPrecioDesde);
    if (hotel) {
      return formatDate(hotel.fechaInicio) + ' - ' + formatDate(hotel.fechaFin);
    } else {
      return '';
    }
  });

  function getServiciosPaqueteBasico() {
    if (!agrupacionesServicios.value) {
      return [];
    }
    return Object.values(agrupacionesServicios.value.flatMap(agrup => agrup.servicios).filter(s => s.paqueteBasico));
  }

  function getServiceIcon(servicio) {
    const agrupacionServicios = agrupacionesServicios.value.find(
      agrupacion => agrupacion.id === servicio.agrupacion?.id
    );

    if (!agrupacionServicios) {
      return { type: TipoIconoServicio.IMAGE, value: '' };
    }

    if (agrupacionServicios.icono) {
      return { type: TipoIconoServicio.IMAGE, value: agrupacionServicios.icono };
    } else {
      const serviciosMetaData = getCategoriasServiciosMetaData().find(
        ({ selector }) => selector === agrupacionServicios.tipo
      );
      if (serviciosMetaData?.icono) {
        return { type: TipoIconoServicio.ICON, value: serviciosMetaData.icono };
      }
    }
  }

  function toggleCollapse() {
    isCollapsed.value = !isCollapsed.value;
  }
</script>

<style lang="scss" scoped>
  .card-persona {
    border-radius: var(--bs-border-radius-xl);
    border: 1px solid rgba(var(--bs-primary-rgb), 0.2);
    background: white;

    .persona-header {
      background-color: rgba(var(--bs-primary-rgb), 0.1);
      border-top-left-radius: var(--bs-border-radius-xl);
      border-top-right-radius: var(--bs-border-radius-xl);

      &.collapsed {
        border-bottom-left-radius: var(--bs-border-radius-xl);
        border-bottom-right-radius: var(--bs-border-radius-xl);
      }

      .icono-persona {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;

        .icono-ocupado {
          width: 24px !important;
          height: 24px !important;
          font-size: 12px !important;
          border-width: 1px !important;
        }
      }
    }

    .servicio-item {
      .servicio-icon {
        width: 14px;
        height: 14px;
        font-size: 14px;
        margin-right: 8px;
        margin-top: 2px;
        flex-shrink: 0;
      }

      .descripcion-servicio {
        margin-bottom: 3px;
      }

      &:last-child {
        margin-bottom: 0 !important;
      }
    }

    .divisor-horizontal {
      border-top: 1px solid rgba(var(--bs-primary-rgb), 0.2);
    }
  }

  .card-total-final {
    background-color: rgba(var(--bs-primary-rgb), 0.1);
    border-radius: var(--bs-border-radius-xl);
  }

  .text-primary-light {
    color: rgba(var(--bs-primary-rgb), 0.8);
  }

  .collapse-enter-active,
  .collapse-leave-active {
    transition: all 0.3s ease;
  }

  .collapse-enter-from,
  .collapse-leave-to {
    opacity: 0;
    max-height: 0;
  }

  .collapse-enter-to,
  .collapse-leave-from {
    opacity: 1;
    max-height: 1000px;
  }

  .btn-recibo {
    border: none;
    background: none;
    display: flex;
    align-items: center;
  }

  .alojamiento-nombre {
    &:hover {
      cursor: pointer;
      text-decoration: underline;
    }
  }
</style>
